// Scheduled delivery (handoff §7l.6): the shapes /api/report-schedules
// returns, and the words the dialog and the Reports home list use for them.
import { FILTER_WORDS, type FilterKey, type Filters } from './model'

export type Cadence = 'daily' | 'weekly' | 'monthly'
export type Expiry = '1 week' | '1 month' | '3 months'
export type Outcome = 'delivered' | 'part' | 'not_sent' | 'skipped'

export const CADENCES: { k: Cadence; label: string }[] = [
  { k: 'daily', label: 'Daily' },
  { k: 'weekly', label: 'Weekly' },
  { k: 'monthly', label: 'First of the month' },
]
export const EXPIRIES: Expiry[] = ['1 week', '1 month', '3 months']

// What the server stores for a recipient; the labels are added when read.
export type RecipientRef = { kind: 'person'; id: string } | { kind: 'group'; id: string } | { kind: 'email'; email: string }
export type Recipient = RecipientRef & { label: string; sub: string }

export interface Match {
  kind: 'person' | 'group'
  id: string
  label: string
  sub: string
}

export interface Schedule {
  id: string
  report_key: string
  report_name: string
  date_mode: 'period' | 'season'
  filters: Partial<Filters>
  team_name: string | null
  cadence: Cadence
  only_if_activity: boolean
  link_expiry: Expiry
  // null: withheld from a caller without shared_reports (SCHED-SCOPE-1).
  recipients: Recipient[] | null
  enabled: boolean
  link_generation: number
  version: number
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
  last_run: { ran_at: string; outcome: Outcome } | null
  run_count: number
}

export interface Run {
  id: string
  due_at: string
  ran_at: string
  period_from: string
  period_to: string
  outcome: Outcome
  // All four null for a caller without shared_reports (SCHED-SCOPE-1).
  recipient_count: number | null
  delivered_count: number | null
  opened_count: number | null
  failures: { label: string; address: string | null; reason: string }[] | null
  reason: string | null
}

// The server's own test (services/report-schedules.js EMAIL_RE), so Add
// refuses exactly what the server would.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const cadenceLabel = (c: Cadence) => CADENCES.find((x) => x.k === c)?.label || c

export const recipientKey = (r: RecipientRef) => (r.kind === 'email' ? `email:${r.email.toLowerCase()}` : `${r.kind}:${r.id}`)

export const toRef = (r: RecipientRef): RecipientRef => (r.kind === 'email' ? { kind: 'email', email: r.email } : { kind: r.kind, id: r.id })

export const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`

// "Active · Daily · to 3 recipients", one line per schedule in the dialog's
// "Already scheduled" block (§7l.6).
export const existingLine = (s: Schedule) =>
  [s.enabled ? 'Active' : 'Paused', cadenceLabel(s.cadence), s.recipients ? `to ${plural(s.recipients.length, 'recipient')}` : null].filter(Boolean).join(' · ')

// Where the list says who a schedule goes to. Recipients are for ADMIN and
// SYSADMIN only (SCHED-SCOPE-1); anyone else is told so, not shown an empty list.
export const recipientsText = (s: Schedule) => (s.recipients ? `to ${s.recipients.map((r) => r.label).join(', ')}` : 'recipients shown to ADMIN and SYSADMIN only')

export const activeCount = (xs: Schedule[]) => xs.filter((x) => x.enabled).length

export const scheduleSummary = (xs: Schedule[]) => plural(activeCount(xs), 'active schedule')

// A report is SCHEDULED while at least one of its schedules is on.
export const scheduledKeys = (xs: Schedule[]) => new Set(xs.filter((x) => x.enabled).map((x) => x.report_key))

// The send time in the zone the server sends in: the workspace's operating
// time zone, or UTC while none is set (services/report-schedules.js).
export const sendTime = (tz: string | null) => `9:00 pm ${tz || 'UTC'}`

// A timestamp in the viewer's own words: "Mon, 28 Sept, 9:00 pm".
export function whenText(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  const time = d.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s+/g, ' ').toLowerCase()
  return `${day}, ${time}`
}

// V4's three outcome pills, and a fourth, quieter one for a run that sent
// nothing because nothing happened (§7l.6 "records the run as skipped").
export const OUTCOMES: Record<Outcome, { label: string; bg: string; fg: string }> = {
  delivered: { label: 'Delivered', bg: '#e8efd6', fg: '#3f4a2c' },
  part: { label: 'Part delivered', bg: '#fbeecd', fg: '#7a5410' },
  not_sent: { label: 'Not sent', bg: '#f8ded0', fg: '#8c491a' },
  skipped: { label: 'Skipped', bg: '#f2ece1', fg: '#8a8073' },
}

// "2 of 3 opened the link", never "read" (§7l.6): an open is evidence the
// link works, not a read receipt. A run that reached nobody has no count.
export function openedText(r: Run): string {
  if (r.outcome === 'not_sent' || r.outcome === 'skipped' || !r.delivered_count || r.opened_count === null) return '—'
  return `${r.opened_count} of ${r.delivered_count} opened the link`
}

// The latest run, always on the row (§7l.6): "Last sent …" when it sent,
// else what it did instead, so a skipped night does not read as a send.
export function lastSentText(s: Schedule): string {
  const r = s.last_run
  if (!r) return 'Not sent yet'
  if (r.outcome === 'delivered' || r.outcome === 'part') return `Last sent ${whenText(r.ran_at)}`
  return `Last run ${whenText(r.ran_at)} · ${OUTCOMES[r.outcome]?.label || r.outcome}`
}

// A schedule's filters in words, as a saved view says them.
export function filterWords(keys: string[], f: Partial<Filters>, teamName: string | null): string[] {
  return keys.map((k) =>
    k === 'team' ? (f.team ? teamName || 'Chosen team' : 'All teams') : f[k as FilterKey] || FILTER_WORDS[k as FilterKey]?.all || 'All',
  )
}

// What a run covers. A scheduled report never sends the dates on screen when
// it was set up: each run covers its own cadence's window (DECISIONS, 27f).
export function coversText(dateMode: 'period' | 'season', cadence: Cadence): string {
  if (dateMode === 'season') return 'the season to date'
  return cadence === 'daily' ? 'the day it is sent' : cadence === 'weekly' ? 'the pay week that ends the day it is sent' : 'the month before the 1st it is sent'
}

// "Daily Harvest Summary · All teams · Daily · to Jane Rata, Supervisors".
export const scheduleTitleLine = (s: Schedule, keys: string[]) =>
  [s.report_name, ...filterWords(keys, s.filters, s.team_name), s.date_mode === 'season' ? 'Whole season' : null].filter(Boolean).join(' · ')

// A run's note: what went wrong, named, and for a part delivery, who missed out.
export function runNote(r: Run): string {
  const parts: string[] = []
  if (r.reason) parts.push(r.reason)
  if (r.failures?.length) parts.push(r.failures.map((f) => `${f.address && f.address !== f.label ? `${f.label} (${f.address})` : f.label}: ${f.reason}`).join('; '))
  return parts.join(' · ')
}
